<template>
  <div class="companies">
    <SectionHeader>
      <template v-slot:breadcrumbs>
        <BreadCrumbs :items="[{ text: `${$t('meta.routes')}`, name: 'company_routes' }, { text: route.name }]" />
      </template>
    </SectionHeader>

    <div class="body-content cf">
      <CompanyNav />
      <div class="body">
        <div class="content-wrap">
          <header class="content-header cf">
            <h3>{{ $t('meta.overview') }}</h3>
            <div class="header-meta">
              <Link :to="`/routes/${route.uuid}`" class="route-link">
                <span class="material-icons">launch</span>
                <span>{{ $t('routes.openRoute') }}</span>
              </Link>
            </div>
          </header>
          <div class="content-body">
            <LoadingSpinner v-if="loading" />
            <template v-else>
              <form @submit.prevent="updateRoute" class="form" method="post">
                <div class="form-wrap">
                  <label for="name">{{ $t('meta.name') }}</label>
                  <input v-model="route.name" class="form-input" type="text" name="name" id="name" />
                </div>

                <div class="form-wrap">
                  <label for="containers">{{ $t('routes.locationsAndContainers') }}</label>
                  <draggable v-model="route.stops" group="stops" class="route-containers">
                    <div v-for="stop in route.stops" :key="stop.uuid" class="drag-container">
                      <div v-if="stop.position_type == 'container'">
                        <p>{{ stop.group_name }}</p>
                        <ul>
                          <li v-for="container in stop.containers" :key="container.uuid">
                            {{ container.container.name }} ({{ container.container.type }})
                            <span @click="delete_container(stop, container)" class="material-icons">delete</span>
                          </li>
                        </ul>
                        <span @click="delete_stop(stop)" class="material-icons">delete</span>
                      </div>
                    </div>
                  </draggable>
                </div>

                <div class="form-wrap form-wrap--sub">
                  <LoadingSpinner v-if="loadingContainerGroups" />
                  <template v-else>
                    <h3>
                      {{ $t('routes.addContainerGroup') }}
                    </h3>
                    <label for="algorithm">{{ $t('routes.addContainerGroup') }}</label>
                    <LiveSearch
                      :onSelected="onSelectedGroup"
                      :onSearch="onSearchGroups"
                      :itemsWithSections="formattedContainerGroupsForDropdown"
                    />
                    <a @click="addContainerGroup" class="button button-green button-round button-small">{{
                      $t('routes.addContainerGroup')
                    }}</a>
                  </template>
                </div>

                <input type="submit" :value="$t('meta.submit')" class="button button-green button-100 button-round" />
              </form>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable'

import BreadCrumbs from '@/components/BreadCrumbs'
import LiveSearch from '@/components/LiveSearch.vue'
import LoadingSpinner from '@/components/LoadingSpinner'
import SectionHeader from '@/components/SectionHeader'
import CompanyNav from '@/components/company_nav'

import containerGroupsApi from '@/api/containerGroups'
import routesApi from '@/api/routes'

export default {
  name: 'company_route_events',
  inject: ['companyUser'],
  data() {
    return {
      loading: true,
      loadingContainerGroups: true,
      route: {},
      containerGroup: {},
      containerGroups: [],
    }
  },
  components: {
    BreadCrumbs,
    CompanyNav,
    draggable,
    LiveSearch,
    LoadingSpinner,
    SectionHeader,
  },
  computed: {
    formattedContainerGroupsForDropdown() {
      if (!this.containerGroups) return {}

      const objFormat = {
        null: this.containerGroups.map(group => this.containerGroupInList(group)),
      }
      const groupsAboveThreshold = this.containerGroups.filter(group => group.filtered_containers.length > 0)
      const aboveThresholdTitle = `${this.$t('containerGroups.containerFillLevel')} > ${this.percentageFormat(
        this.companyUser.user.report_threshold,
      )}:`
      return groupsAboveThreshold.length > 0
        ? {
            ...objFormat,
            [aboveThresholdTitle]: groupsAboveThreshold.map(group => this.groupWithContainersAboveThreshold(group)),
          }
        : objFormat
    },
  },
  methods: {
    async getData() {
      this.route = await routesApi.getDetailed(this.$route.params.route_uuid)
      this.loading = false

      this.containerGroups = await containerGroupsApi.getFromSearch('', {
        route_uuid: this.$route.params.route_uuid,
      })
      this.loadingContainerGroups = false
    },
    updateRoute: async function () {
      const result = await routesApi.update(this.route.uuid, this.route.name)
      if (result.success) {
        this.triggerRecreation()
      }
    },
    async triggerRecreation() {
      this.loading = true
      const containerIds = this.route.stops
        .filter(stop => stop.position_type == 'container')
        .map(stop => stop.containers.map(c => c.container.uuid))
        .flat()

      await routesApi.recreate(this.route.uuid, containerIds)
      await this.getData()
      this.loading = false
    },
    delete_stop(stop) {
      this.route.stops.splice(this.route.stops.indexOf(stop), 1)
    },
    delete_container(stop, container) {
      stop.containers.splice(stop.containers.indexOf(container), 1)
    },
    addContainerGroup() {
      const containers = this.containerGroup.containers.map(container => ({
        container: {
          name: container.name,
          preformatted: container.preformatted,
          type: container.type,
          uuid: container.uuid,
        },
      }))
      this.route.stops.push({
        uuid: this.containerGroup.uuid,
        name: this.containerGroup.name,
        preformatted: this.containerGroup.preformatted,
        position_type: 'container',
        containers: containers,
      })
      this.containerGroup = ''
    },
    onSelectedGroup(selected) {
      this.containerGroup = selected.value
    },
    async onSearchGroups(searchText) {
      this.containerGroups = await containerGroupsApi.getFromSearch(searchText, {
        route_uuid: this.$route.params.route_uuid,
      })
    },
    groupWithContainersAboveThreshold(cGroup) {
      const containers = cGroup.filtered_containers.map(
        container =>
          `${this.percentageFormat(container.fill_level)} (${container.fraction || this.$t('meta.notAvailable')})`,
      )
      const formatted = `${cGroup.name}: ${containers.join('; ')}`
      return { ...cGroup, formattedTitle: formatted }
    },
    containerGroupInList(cGroup) {
      return {
        ...cGroup,
        formattedTitle: cGroup.name,
        formattedSubtitle: `, ${cGroup.preformatted} (${this.$t('meta.project')} ${cGroup.project.name})`,
      }
    },
    percentageFormat(number) {
      return `${Math.round(parseFloat(number) * 100)}%`
    },
  },
  mounted() {
    this.getData()
  },
}
</script>

<style lang="scss">
.route-containers {
  margin: 0;
  padding: 0;

  .drag-container {
    list-style-type: none;
    border: 1px solid #f1f1f1;
    padding: 20px;
    margin-bottom: 10px;
    position: relative;

    .material-icons {
      color: #868686;
      cursor: pointer;
      position: absolute;
      right: 20px;
      top: 15px;

      &:hover {
        color: #444;
      }
    }

    p {
      margin: 0 0 20px 0;
      font-weight: 600;
      font-size: 18px;
    }

    ul {
      margin: 0;
      padding: 0;

      li {
        font-size: 14px;
        padding: 14px;
        background: #fafafa;
        position: relative;
        list-style-type: none;
        margin-bottom: 5px;

        .material-icons {
          position: absolute;
          right: 15px;
          top: 10px;
          color: #868686;
          cursor: pointer;
        }

        .material-icons:hover {
          color: #444;
        }
      }

      li:last-child {
        margin-bottom: 0;
      }
    }
  }

  li:last-child {
    margin-bottom: 0;
  }
}

.route-link {
  padding: 0.45rem 0.65rem;
  color: #444;
  display: flex;
  align-items: center;

  span.material-icons {
    margin-right: 0.275rem;
  }
}
</style>
